import React, { useEffect, useState } from 'react';
import { getConfig } from '../config';

type Workspace = { id: number; name: string };

type Row = {
  workspaceId: number;
  workspaceName: string;
  users: number;
  cameras: number;
  recordings: number;
  recordingHours: number;
  comments: number;
  textComments: number;
  audioComments: number;
  commentsWithDrawings: number;
};

const Card: React.FC<{ title: string; value: React.ReactNode; subtitle?: string }> = ({ title, value, subtitle }) => (
  <div className="bg-white rounded-lg shadow p-4 flex flex-col gap-1 min-w-[220px]">
    <div className="text-sm text-gray-500">{title}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {subtitle && <div className="text-xs text-gray-400">{subtitle}</div>}
  </div>
);

const AnalyticsWorkspaceInfo: React.FC = () => {
  const { apiBase } = getConfig();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [start, setStart] = useState<string>(''); // YYYY-MM-DD
  const [end, setEnd] = useState<string>('');     // YYYY-MM-DD
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/superadmin/workspaces/options`, { credentials: 'include' });
        if (res.ok) setWorkspaces(await res.json());
      } catch {}
    })();
  }, [apiBase]);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const url = new URL(`${apiBase}/superadmin/analytics/workspace-info`);
      if (workspaceId) url.searchParams.set('workspaceId', workspaceId);
      if (start) url.searchParams.set('start', start);
      if (end) url.searchParams.set('end', end);
      url.searchParams.set('limit', '200');
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = (await res.json()) as Row[];
      setRows(data);
    } catch (e: any) {
      setErr(e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load: all-time
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const hasRange = Boolean(start && end);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Workspace Information</h1>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm">Workspace</label>
          <select
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {workspaces.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">Start</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="border rounded px-2 py-1 text-sm" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm">End</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="border rounded px-2 py-1 text-sm" />
        </div>
        <button
          className="bg-blue-600 text-white text-sm rounded px-3 py-2 disabled:opacity-50"
          disabled={Boolean((start && !end) || (!start && end))}
          onClick={load}
        >
          Apply Filters
        </button>
      </div>

      {loading && <div>Loading…</div>}
      {err && <div className="text-red-600">Error: {err}</div>}
      {!loading && !err && (
        <div className="space-y-6">
          {rows.map((r) => (
            <div key={r.workspaceId} className="border rounded-lg p-4 bg-gray-50">
              <div className="mb-3 text-lg font-semibold">{r.workspaceName}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <Card title="Members" value={r.users} />
                <Card title="Cameras" value={r.cameras} />
                <Card title="Recordings" value={r.recordings} subtitle={hasRange ? 'Selected range' : 'All time'} />
                <Card title="Recording Hours" value={r.recordingHours.toFixed(1)} subtitle={hasRange ? 'Selected range' : 'All time'} />
                <Card title="Comments" value={r.comments} subtitle={hasRange ? 'Selected range' : 'All time'} />
                <Card title="Text Comments" value={r.textComments} subtitle={hasRange ? 'Selected range' : 'All time'} />
                <Card title="Audio Comments" value={r.audioComments} subtitle={hasRange ? 'Selected range' : 'All time'} />
                <Card title="Comments with Drawings" value={r.commentsWithDrawings} subtitle={hasRange ? 'Selected range' : 'All time'} />
              </div>
            </div>
          ))}
          {rows.length === 0 && <div>No workspaces found.</div>}
        </div>
      )}
    </div>
  );
};

export default AnalyticsWorkspaceInfo;


